import Big from 'big.js';
import * as bootstrap from 'bootstrap';
import * as models from 'smg/admin/models';
import axios from 'smg/axios';
import { ShowError } from 'smg/errors';
import { DollarFormat } from 'smg/util';

// undefined spends the whole balance (omitted from the preview and the
// order). Big(0) spends none. Any other amount spends up to that amount.
export type AvailableDiscountChoice = InstanceType<typeof Big> | undefined;

export interface AvailableDiscountHandle {
    // Re-fetch the resolved customer's available discount. Empty userID
    // hides the section and returns the choice to spend-all.
    refresh(userID: string): void;
    // The choice the preview and the order POST both send.
    snapshot(): AvailableDiscountChoice;
    // Resolves when the lookup started by the current refresh finishes.
    // Place Order waits on this before the preview: a failed lookup forces
    // spend-none, which re-queues that preview.
    settled(): Promise<void>;
    // Whether no lookup is in flight right now, so a caller that awaited
    // settled() can tell a newer lookup started while it waited.
    isSettled(): boolean;
}

export interface AvailableDiscountDeps {
    onChoiceChange: (apply: AvailableDiscountChoice) => void;
}

export function SameAvailableDiscountChoice(a: AvailableDiscountChoice, b: AvailableDiscountChoice): boolean {
    if (a === undefined || b === undefined) {
        return a === b;
    }
    return a.eq(b);
}

export function SetupAvailableDiscount(deps: AvailableDiscountDeps): AvailableDiscountHandle {
    const $section = document.getElementById('checkout-section-available-discount') as HTMLElement;
    const $available = document.getElementById('available-discount-available') as HTMLElement;
    const $input = document.getElementById('available-discount-input') as HTMLInputElement;
    const $useYes = document.getElementById('available-discount-use-yes') as HTMLInputElement;
    const $useNo = document.getElementById('available-discount-use-no') as HTMLInputElement;
    const $amountCollapse = document.getElementById('available-discount-amount-collapse') as HTMLFieldSetElement;
    const $body = document.getElementById('available-discount-body') as HTMLElement;
    const $failed = document.getElementById('available-discount-failed') as HTMLElement;
    const $retry = document.getElementById('available-discount-retry') as HTMLButtonElement;

    const amountCollapse = bootstrap.Collapse.getOrCreateInstance($amountCollapse, { toggle: false });

    let available = Big(0);
    // undefined = spend all. A failed lookup forces Big(0) while the section
    // stays hidden, so the snapshot cannot be derived from the radios alone.
    let choice: AvailableDiscountChoice = undefined;
    let lookup: 'idle' | 'loading' | 'ready' | 'failed' = 'idle';
    // Increments on every refresh() so a fast customer switch can discard a
    // stale in-flight response.
    let fetchSeq = 0;
    // The customer the last refresh was for, so Retry can repeat it.
    let lookupUserID = '';
    const settleWaiters: (() => void)[] = [];

    function setChoice(next: AvailableDiscountChoice) {
        if (SameAvailableDiscountChoice(choice, next)) {
            return;
        }
        choice = next;
        deps.onChoiceChange(choice);
    }

    // Yes at or above the full balance, and an empty Yes, both mean spend
    // all. The required attribute still blocks an empty Yes at submit.
    function choiceFromForm(): AvailableDiscountChoice {
        if (!$useYes.checked) {
            return Big(0);
        }
        const v = parseFloat($input.value);
        if (isNaN(v) || v <= 0) {
            return undefined;
        }
        const amount = Big(v);
        if (amount.gte(available)) {
            return undefined;
        }
        return amount;
    }

    function syncRadio() {
        if ($useYes.checked) {
            amountCollapse.show();
            // Fieldset gates the input's `required` so HTML5 validation
            // only kicks in when the admin opted into applying the discount.
            $amountCollapse.disabled = false;
        } else {
            amountCollapse.hide();
            $amountCollapse.disabled = true;
            // The native is-invalid marker survives the hide otherwise.
            $input.dispatchEvent(new Event('clearinvalid'));
        }
        setChoice(choiceFromForm());
    }

    function resetRadioToDefault() {
        $useYes.checked = true;
        $useNo.checked = false;
        amountCollapse.show();
        // syncRadio's No-branch leaves the fieldset disabled. Switching
        // back to Yes has to re-enable it. The hidden-section path disables
        // it again afterwards so validation skips the field.
        $amountCollapse.disabled = false;
    }

    function finishLookup(mySeq: number, state: 'ready' | 'failed') {
        if (mySeq !== fetchSeq) {
            return;
        }
        lookup = state;
        const waiters = settleWaiters.splice(0, settleWaiters.length);
        for (const waiter of waiters) {
            waiter();
        }
    }

    $input.addEventListener('input', () => setChoice(choiceFromForm()));
    $useYes.addEventListener('change', syncRadio);
    $useNo.addEventListener('change', syncRadio);
    $retry.addEventListener('click', () => void fetchAvailable(lookupUserID));

    async function fetchAvailable(userID: string) {
        const mySeq = ++fetchSeq;
        lookup = 'loading';
        lookupUserID = userID;
        // Reset to a safe baseline before awaiting anything. Otherwise a
        // slow or failing lookup for customer B could leave customer A's
        // discount applied to B's cart.
        available = Big(0);
        $section.classList.add('d-none');
        $failed.classList.add('d-none');
        $body.classList.remove('d-none');
        $input.value = '';
        resetRadioToDefault();
        // Section hidden means the required input must not block form
        // validation. resetRadioToDefault re-enables the fieldset, so this
        // override has to come after it.
        $amountCollapse.disabled = true;
        setChoice(undefined);
        if (!userID) {
            finishLookup(mySeq, 'ready');
            return;
        }
        try {
            const resp = await axios.get('/users', {
                params: {
                    'q': `id:${userID}`,
                    'data': 'availableDiscount',
                    'max': 1,
                },
            });
            if (mySeq !== fetchSeq) {
                return;
            }
            const user = models.UsersHyejooResponse.fromJSON(resp.data).users[0];
            const raw = user?.availableDiscount ?? Big(0);
            const next = raw.lte(0) ? Big(0) : raw;
            if (next.gt(0)) {
                available = next;
                $available.textContent = DollarFormat(available);
                // Default is the whole balance. Leaving the choice undefined
                // omits it from the request, which the server reads as all.
                // Rounded up to the cent so a sub-cent ledger balance still
                // fills a valid input, which choiceFromForm reads as all.
                const cents = available.round(2, Big.roundUp).toFixed(2);
                $input.max = cents;
                $input.value = cents;
                $section.classList.remove('d-none');
                $amountCollapse.disabled = !$useYes.checked;
            }
            finishLookup(mySeq, 'ready');
        } catch (err) {
            if (mySeq !== fetchSeq) {
                return;
            }
            // Spend none while sales cannot see the balance; the section shows
            // the failure with Retry. Notify before resolving settled() so the
            // re-queued preview is the one Place Order waits on, and before
            // ShowError so a throw there can't leave the choice on spend-all.
            setChoice(Big(0));
            $body.classList.add('d-none');
            $failed.classList.remove('d-none');
            $section.classList.remove('d-none');
            finishLookup(mySeq, 'failed');
            ShowError(err);
        }
    }

    return {
        refresh: userID => void fetchAvailable(userID),
        snapshot: () => choice,
        isSettled: () => lookup !== 'loading',
        settled: () => {
            if (lookup !== 'loading') {
                return Promise.resolve();
            }
            return new Promise(resolve => {
                settleWaiters.push(resolve);
            });
        },
    };
}
